import { ChangeEvent, FC, useCallback, useState } from 'react';

import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import { zIndex } from 'styles';

import { sendContactForm } from 'client';
import { validateEmail } from 'components/ContactForm/utils';
import {
  Button,
  EmailInput,
  EmailInputContainer,
  FormControlContainer,
  ModalContent,
} from 'components/Header/ContactModal.style';

type ResearchQuestionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  pageTag: string;
};

export const ResearchQuestionModal: FC<ResearchQuestionModalProps> = ({
  isOpen,
  onClose,
  pageTag,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [questions, setQuestions] = useState('');
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [questionsError, setQuestionsError] = useState(false);
  const [agreeMarketing, setAgreeMarketing] = useState(false);
  const [agreeProcessing, setAgreeProcessing] = useState(false);
  const [marketingError, setMarketingError] = useState(false);
  const [processingError, setProcessingError] = useState(false);

  const handleClose = useCallback(() => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setQuestions('');
    setFirstNameError(false);
    setLastNameError(false);
    setIsEmailValid(true);
    setQuestionsError(false);
    setAgreeMarketing(false);
    setAgreeProcessing(false);
    setMarketingError(false);
    setProcessingError(false);
    onClose();
  }, [onClose]);

  const onFirstNameChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFirstName(e.currentTarget.value);
    setFirstNameError(false);
  }, []);

  const onLastNameChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLastName(e.currentTarget.value);
    setLastNameError(false);
  }, []);

  const onEmailChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setEmail(e.currentTarget.value);
      if (!isEmailValid) setIsEmailValid(true);
    },
    [isEmailValid],
  );

  const onQuestionsChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setQuestions(e.currentTarget.value);
    setQuestionsError(false);
  }, []);

  const onMarketingChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setAgreeMarketing(checked);
    if (checked) setMarketingError(false);
  }, []);

  const onProcessingChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setAgreeProcessing(checked);
    if (checked) setProcessingError(false);
  }, []);

  const onSubmit = useCallback(() => {
    const firstNameValid = firstName.trim().length > 0;
    const lastNameValid = lastName.trim().length > 0;
    const emailValid = validateEmail(email);
    const questionsValid = questions.trim().length > 0;

    setFirstNameError(!firstNameValid);
    setLastNameError(!lastNameValid);
    setIsEmailValid(emailValid);
    setQuestionsError(!questionsValid);
    setMarketingError(!agreeMarketing);
    setProcessingError(!agreeProcessing);

    if (
      !firstNameValid ||
      !lastNameValid ||
      !emailValid ||
      !questionsValid ||
      !agreeMarketing ||
      !agreeProcessing
    ) {
      return;
    }

    setIsLoading(true);

    const name = `${firstName.trim()} ${lastName.trim()}`;
    const message = [
      'Research question inquiry',
      `Questions: ${questions.trim()}`,
      'Marketing consent: yes',
      'Data processing consent: yes',
    ].join('\n');

    sendContactForm(email, pageTag, name, message)
      .then(() => {
        setIsLoading(false);
        handleClose();
      })
      .catch(() => {
        setIsLoading(false);
        setIsEmailValid(false);
      });
  }, [
    firstName,
    lastName,
    email,
    questions,
    agreeMarketing,
    agreeProcessing,
    pageTag,
    handleClose,
  ]);

  return (
    <Modal
      sx={{
        zIndex: zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="research-question-modal-title"
      aria-describedby="research-question-modal-description"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0,0,0,0.2)',
          },
        },
      }}
    >
      <ModalContent
        sx={{ maxWidth: { xs: 'unset', sm: 560 }, px: { xs: 3, sm: 4 }, py: { xs: 3, sm: 4 } }}
      >
        <Typography id="research-question-modal-title" variant="h6" component="h2">
          Questions about the research?
        </Typography>
        <Typography id="research-question-modal-description" sx={{ mt: 2 }}>
          Please use the form below to submit your questions, comments, or inquiries regarding our
          research. We look forward to hearing from you!
        </Typography>
        <FormControlContainer
          sx={{
            mt: 3,
            gap: 1.5,
            width: '100%',
            flexDirection: 'column',
            alignItems: 'stretch',
            '@media (min-width: 600px)': {
              flexDirection: 'column',
              alignItems: 'stretch',
              flexBasis: 'auto',
            },
          }}
        >
          <EmailInputContainer>
            <EmailInput
              error={firstNameError}
              disabled={isLoading}
              fullWidth
              type="text"
              value={firstName}
              placeholder="First Name*"
              onChange={onFirstNameChange}
              aria-invalid={firstNameError}
              required
            />
          </EmailInputContainer>

          <EmailInputContainer>
            <EmailInput
              error={lastNameError}
              disabled={isLoading}
              fullWidth
              type="text"
              value={lastName}
              placeholder="Last Name*"
              onChange={onLastNameChange}
              aria-invalid={lastNameError}
              required
            />
          </EmailInputContainer>

          <EmailInputContainer>
            <EmailInput
              error={!isEmailValid}
              disabled={isLoading}
              fullWidth
              type="email"
              value={email}
              placeholder="Email*"
              onChange={onEmailChange}
              aria-describedby="research-email-error"
              aria-invalid={!isEmailValid}
              required
            />
            {!isEmailValid && (
              <span id="research-email-error" className="sr-only">
                Please enter a valid email address.
              </span>
            )}
          </EmailInputContainer>

          <EmailInputContainer>
            <EmailInput
              error={questionsError}
              disabled={isLoading}
              fullWidth
              multiline
              minRows={4}
              value={questions}
              placeholder="Questions*"
              onChange={onQuestionsChange}
              aria-invalid={questionsError}
              required
            />
          </EmailInputContainer>

          <Typography
            variant="body2"
            sx={{ mt: 1, fontSize: 13, lineHeight: 1.5, color: 'text.secondary' }}
          >
            AILiveSim is committed to protecting and respecting your privacy, and we&apos;ll only
            use your personal information to administer your account and to provide the products
            and services you requested from us. From time to time, we would like to contact you
            about our products and services, as well as other content that may be of interest to
            you. If you consent to us contacting you for this purpose, please tick below to say
            how you would like us to contact you:
          </Typography>

          <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={
              <Checkbox
                checked={agreeMarketing}
                onChange={onMarketingChange}
                disabled={isLoading}
                required
                sx={{ pt: 0.5 }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: 14, lineHeight: 1.5 }}>
                I agree to receive other communications from AILiveSim.*
              </Typography>
            }
          />
          {marketingError && (
            <Typography variant="caption" color="error">
              You must agree to receive communications from AILiveSim.
            </Typography>
          )}

          <Typography
            variant="body2"
            sx={{ fontSize: 13, lineHeight: 1.5, color: 'text.secondary' }}
          >
            In order to provide you the content requested, we need to store and process your
            personal data. If you consent to us storing your personal data for this purpose,
            please tick the checkbox below.
          </Typography>

          <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0 }}
            control={
              <Checkbox
                checked={agreeProcessing}
                onChange={onProcessingChange}
                disabled={isLoading}
                required
                sx={{ pt: 0.5 }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: 14, lineHeight: 1.5 }}>
                I agree to allow AILiveSim to store and process my personal data.*
              </Typography>
            }
          />
          {processingError && (
            <Typography variant="caption" color="error">
              You must agree to allow us to store and process your personal data.
            </Typography>
          )}

          <Typography
            variant="caption"
            sx={{ fontSize: 12, lineHeight: 1.5, color: 'text.secondary' }}
          >
            You may unsubscribe from these communications at any time. For more information on
            how to unsubscribe, our privacy practices, and how we are committed to protecting and
            respecting your privacy, please review our{' '}
            <a
              href="http://localhost:3000/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              Privacy Policy
            </a>
            .
          </Typography>

          <Button
            disabled={isLoading}
            size="large"
            onClick={onSubmit}
            aria-label="Submit research question"
            sx={{ width: '100% !important', mt: 1 }}
          >
            Submit
          </Button>
        </FormControlContainer>
      </ModalContent>
    </Modal>
  );
};
